/**
 * Posteingang - Butler21-Style Dokumentenverwaltung
 * 3-Spalten-Layout: Liste | Zuordnung | Vorschau
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Inbox, Upload, FileText, Sparkles, X, Check, ChevronRight,
  Loader2, User, Briefcase, Calendar, Tag, Clock,
  Save, SkipForward, MessageSquare, Trash2, Plus, UserPlus, Search, GripVertical,
  Phone, Send, RefreshCw, Mail, Printer, Building2, MessageCircle, PhoneMissed, Reply,
  CheckSquare, UserCheck,
} from 'lucide-react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DND_TYPES } from '@/lib/dndTypes';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import PosteingangVorschau from './posteingang/PosteingangVorschau';
import PosteingangBatchDialoge from './posteingang/PosteingangBatchDialoge';
import PosteingangZuordnungsForm from './posteingang/PosteingangZuordnungsForm';
import PosteingangAutoAktion from './posteingang/PosteingangAutoAktion';
import QuickAssign from '@/components/QuickAssign';
import EmailComposer from '@/components/EmailComposer';
import { useTabs } from '@/contexts/TabContext';
import { TAB_EVENTS } from '@/lib/tabEvents';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

/** Draggable Dokument-Eintrag in der Posteingang-Liste */
function DraggableDocItem({ dok, isSelected, onSelect, onDelete, kiEmpfehlung, onAssigned, klienten }) {
  const [{ isDragging }, dragRef, preview] = useDrag(() => ({
    type: DND_TYPES.DOKUMENT,
    item: { dokument: dok, kiEmpfehlung },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [dok, kiEmpfehlung]);

  // Nativen Drag-Preview ausblenden (DragPreviewLayer übernimmt)
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Klient-Name ermitteln
  const klientName = dok.klient_name || (dok.klient_id && klienten?.find(k => k.id === dok.klient_id)
    ? `${klienten.find(k => k.id === dok.klient_id).vorname || ''} ${klienten.find(k => k.id === dok.klient_id).nachname || ''}`.trim()
    : null);
  const zuordnung = dok.auto_zuordnung;
  const konfidenz = zuordnung?.konfidenz;
  const konfidenzFarbe = konfidenz === 'hoch' ? 'bg-green-100 text-green-700 border-green-200'
    : konfidenz === 'mittel' ? 'bg-amber-100 text-amber-700 border-amber-200'
    : konfidenz === 'niedrig' ? 'bg-slate-100 text-slate-600 border-slate-200'
    : 'bg-blue-100 text-blue-700 border-blue-200';

  return (
    <div
      className={`relative flex items-start gap-1 group transition-all ${
        isDragging ? 'opacity-30' : ''
      }`}
      data-testid={`doc-${dok.id}`}
    >
      {/* Drag Handle */}
      <div
        ref={dragRef}
        className="shrink-0 pt-3 pl-2 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500"
        title="Zum Zuordnen auf einen Tab ziehen"
        data-testid={`doc-drag-${dok.id}`}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </div>

      {/* Dokument-Info (klickbar) */}
      <div
        onClick={() => onSelect(dok)}
        className={`flex-1 p-2 pr-1 cursor-pointer hover:bg-blue-50 transition-colors flex items-start gap-2 ${
          isSelected ? 'bg-blue-100 border-l-4 border-blue-500' : ''
        }`}
      >
        <FileText className={`w-4 h-4 mt-0.5 shrink-0 ${
          dok.dokumententyp === 'zugeordnet' ? 'text-green-500' : 'text-amber-500'
        }`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800 truncate">
            {dok.dateiname || dok.titel || 'Dokument'}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            <span className="text-xs text-slate-500">
              {dok.created_date ? new Date(dok.created_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
            {klientName && (
              <span
                className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full border ${konfidenzFarbe}`}
                title={zuordnung ? `Zuordnung: ${zuordnung.methode} (${zuordnung.konfidenz})` : 'Manuell zugeordnet'}
                data-testid={`doc-klient-badge-${dok.id}`}
              >
                <User className="w-3 h-3" />
                {klientName}
              </span>
            )}
          </div>
        </div>
        {dok.dokumententyp === 'zugeordnet' && <Check className="w-4 h-4 text-green-500 shrink-0" />}
      </div>

      {/* Aktions-Buttons (QuickAssign + Löschen) */}
      <div className="shrink-0 flex items-center gap-0.5 pt-2 pr-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <QuickAssign
          dokument={dok}
          onAssigned={onAssigned}
          kiEmpfehlung={kiEmpfehlung}
          compact
        />
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(dok.id); }}
          className="text-slate-400 hover:text-red-500 p-1"
          title="Löschen"
          data-testid={`doc-delete-${dok.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function PosteingangPage() {
  const [dokumente, setDokumente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [analyseResult, setAnalyseResult] = useState(null);
  
  // Stammdaten für Dropdowns
  const [klienten, setKlienten] = useState([]);
  const [aufgaben, setAufgaben] = useState([]);
  const [jurPersonen, setJurPersonen] = useState([]);
  const [massnahmen, setMassnahmen] = useState([]);  // Maßnahmen des ausgewählten Klienten
  
  // Zuordnungsformular - neu strukturiert
  const [form, setForm] = useState({
    von_institution_id: '',    // 1. Von (Absender)
    klient_id: '',             // 2. Für (Betreuter)
    massnahme_id: '',          // 3. Bezüglich (Maßnahme)
    aufgabe_id: '',            // 4. Aufgabe
    an_institution_id: '',
    ist_antwort_auf: '',
    prioritaet: '',
    tags: '',
    spaeter_bearbeiten: false,
    anderer_bearbeiten: false,
    person_informieren: false,
    termin_anlegen: false,
    notiz: '',
    // Dokumenten-Kategorisierung
    dokumentkategorie: '',  // schriftverkehr, belege, bescheide, antraege_formulare
    bescheid_typ: '',
    bescheid_gueltig_bis: '',
    formular_typ: ''
  });

  // KI-Empfehlung
  const [kiEmpfehlung, setKiEmpfehlung] = useState(null);
  const [loadingKiEmpfehlung, setLoadingKiEmpfehlung] = useState(false);

  // Nachanalyse mit Eckdaten
  const [showNachanalyse, setShowNachanalyse] = useState(false);
  const [nachanalyseEckdaten, setNachanalyseEckdaten] = useState({ nachname: '', vorname: '', hinweis: '' });
  const [nachanalyseLoading, setNachanalyseLoading] = useState(false);
  const [nachanalyseErgebnis, setNachanalyseErgebnis] = useState(null);

  // Formular-Analyse
  const [formularAnalyseLoading, setFormularAnalyseLoading] = useState(false);
  const [formularAnalyseErgebnis, setFormularAnalyseErgebnis] = useState(null);
  const [showFormularAnalyse, setShowFormularAnalyse] = useState(false);

  // Bescheid-Wiedervorlage
  const [wiedervorlageLoading, setWiedervorlageLoading] = useState(false);
  const [wiedervorlageErstellt, setWiedervorlageErstellt] = useState(null);

  // Stapelverarbeitung
  const [batchAnalysing, setBatchAnalysing] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  
  // Auto-Zuordnung
  const [autoZuordnungRunning, setAutoZuordnungRunning] = useState(false);
  const [autoZuordnungResult, setAutoZuordnungResult] = useState(null);
  const [showAutoZuordnungConfirm, setShowAutoZuordnungConfirm] = useState(false);

  // Athena Lernmodus
  const [athenaLernt, setAthenaLernt] = useState(false);
  const [neuesMuster, setNeuesMuster] = useState(null);
  const [athenaStats, setAthenaStats] = useState(null);

  // KI-Analyse Modus (neue intelligente Ansicht)
  const [kiModus, setKiModus] = useState(false);
  const [dokumenteMitAnalyse, setDokumenteMitAnalyse] = useState([]);
  const [kiAnalyseLoading, setKiAnalyseLoading] = useState(false);

  // Nachricht erfassen (WhatsApp/Telefon)
  const [showNachrichtForm, setShowNachrichtForm] = useState(false);
  const [nachrichtText, setNachrichtText] = useState('');
  const [nachrichtQuelle, setNachrichtQuelle] = useState('whatsapp');
  const [nachrichtAbsender, setNachrichtAbsender] = useState('');
  const [nachrichtLoading, setNachrichtLoading] = useState(false);
  const [nachrichtErgebnis, setNachrichtErgebnis] = useState(null);

  // Kanal-Filter für Communication Hub
  const [kanalFilter, setKanalFilter] = useState('alle');

  // Bulk-Zuordnungsmodus
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkKlientId, setBulkKlientId] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleBulkSelect = (dokId) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(dokId)) next.delete(dokId); else next.add(dokId);
      return next;
    });
  };

  const toggleBulkAll = () => {
    const unzugeordnet = gefilterteDokumente.filter(d => !d.klient_id);
    if (bulkSelected.size === unzugeordnet.length) {
      setBulkSelected(new Set());
    } else {
      setBulkSelected(new Set(unzugeordnet.map(d => d.id)));
    }
  };

  const handleBulkZuordnen = async () => {
    if (!bulkKlientId || bulkSelected.size === 0) return;
    const k = klienten.find(kl => kl.id === bulkKlientId);
    const name = k ? `${k.vorname || ''} ${k.nachname || ''}`.trim() : '';
    setBulkSaving(true);
    try {
      const result = await betreuungAPI.bulkKlientZuordnung([...bulkSelected], bulkKlientId, name);
      toast.success(`${result.updated} Dokument(e) zugeordnet`);
      setBulkMode(false);
      setBulkSelected(new Set());
      setBulkKlientId('');
      loadData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBulkSaving(false);
    }
  };

  // Bulk-Loeschen: greift nur bereits (fachlich) unzugeordnete, manuell markierte
  // Dokumente an -- ein Dokument mit gesetzter klient_id wird trotz manueller
  // Checkbox-Auswahl NIE ueber Bulk geloescht (Schutz vor versehentlichem Verlust
  // bereits zugeordneter Post). Nutzt denselben Einzel-Loeschpfad wie
  // handleDeleteDoc (betreuungAPI.deleteDokument), keine neue Backend-Logik.
  const handleBulkDelete = async () => {
    if (bulkSelected.size === 0) return;
    const ausgewaehlteDokumente = gefilterteDokumente.filter(d => bulkSelected.has(d.id));
    const loeschbar = ausgewaehlteDokumente.filter(d => !d.klient_id);
    const geschuetzt = ausgewaehlteDokumente.length - loeschbar.length;

    if (loeschbar.length === 0) {
      toast.error('Keine Löschung möglich: alle ausgewählten Dokumente sind bereits einem Klienten zugeordnet.');
      return;
    }

    const bestaetigung = geschuetzt > 0
      ? window.confirm(
          `${loeschbar.length} unzugeordnete Dokument(e) endgültig löschen?\n\n` +
          `${geschuetzt} ausgewählte(s) Dokument(e) sind bereits einem Klienten zugeordnet und werden NICHT gelöscht.`
        )
      : window.confirm(`${loeschbar.length} Dokument(e) endgültig löschen?`);
    if (!bestaetigung) return;

    setBulkDeleting(true);
    let erfolgreich = 0;
    let fehlgeschlagen = 0;
    for (const dok of loeschbar) {
      try {
        await betreuungAPI.deleteDokument(dok.id);
        erfolgreich += 1;
        if (selectedDoc?.id === dok.id) setSelectedDoc(null);
      } catch {
        fehlgeschlagen += 1;
      }
    }
    setBulkDeleting(false);

    if (erfolgreich > 0 && fehlgeschlagen === 0) {
      toast.success(`${erfolgreich} Dokument(e) gelöscht`);
    } else if (erfolgreich > 0 && fehlgeschlagen > 0) {
      toast.error(`${erfolgreich} gelöscht, ${fehlgeschlagen} fehlgeschlagen`);
    } else {
      toast.error(`Löschen fehlgeschlagen (${fehlgeschlagen} Dokument(e))`);
    }

    setBulkMode(false);
    setBulkSelected(new Set());
    loadData();
  };

  const filteredBulkKlienten = useMemo(() => {
    if (!bulkSearch) return klienten;
    const q = bulkSearch.toLowerCase();
    return klienten.filter(k => 
      `${k.vorname} ${k.nachname}`.toLowerCase().includes(q) ||
      (k.aktenzeichen || '').toLowerCase().includes(q)
    );
  }, [klienten, bulkSearch]);

  // Gefilterte Dokumente basierend auf Kanal-Filter
  const gefilterteDokumente = useMemo(() => {
    if (kanalFilter === 'alle') return dokumente;
    if (kanalFilter === 'verpasste_anrufe') return dokumente.filter(d => d.quelle === 'telefon');
    return dokumente.filter(d => d.kanal === kanalFilter || d.quelle === kanalFilter);
  }, [dokumente, kanalFilter]);

  // E-Mail Composer
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerReplyTo, setComposerReplyTo] = useState(null);

  const openComposer = (replyDoc) => {
    setComposerReplyTo(replyDoc || null);
    setComposerOpen(true);
  };

  // Dialog-States entfallen - stattdessen Tabs verwenden
  const { openTab } = useTabs();
  const navigate = useNavigate();

  // Hilfsfunktion: Sicherstellen, dass Posteingang als Tab registriert ist,
  // bevor ein Kind-Tab geöffnet wird. So kann der Benutzer zwischen Tabs wechseln.
  const openChildTab = useCallback((path, options) => {
    // Zuerst Posteingang als Tab registrieren (falls noch nicht offen)
    openTab('/posteingang', { title: 'Posteingang' });
    // Dann den Kind-Tab öffnen
    openTab(path, options);
    navigate(path);
  }, [openTab, navigate]);

  // Maßnahmenkatalog für Dialoge und Dropdowns
  const [massnahmenkatalog, setMassnahmenkatalog] = useState([]);
  const [massnahmenGruppen, setMassnahmenGruppen] = useState([]);

  // Kontextbezogener KI-Chat für aktuelles Dokument
  const [showDocChat, setShowDocChat] = useState(false);
  const [docChatInput, setDocChatInput] = useState('');
  const [docChatMessages, setDocChatMessages] = useState([]);
  const [docChatLoading, setDocChatLoading] = useState(false);

  // Suchfunktion für Betreute
  const [showKlientSearch, setShowKlientSearch] = useState(false);
  const [klientSearchQuery, setKlientSearchQuery] = useState('');

  const fileInputRef = useRef(null);

  // Daten laden
  const loadData = async () => {
    setLoading(true);
    try {
      const [docs, kl, jp, auf, katalog, gruppen] = await Promise.all([
        betreuungAPI.listDokumente({ dokumententyp: 'posteingang' }),
        betreuungAPI.listKlienten(),
        betreuungAPI.listJuristischePersonen(),
        betreuungAPI.listAufgaben({ status: 'offen' }),
        betreuungAPI.listMassnahmenkatalog().catch(() => []),
        fetch(`${BACKEND_URL}/api/betreuung/massnahmen-gruppen?aktiv=true`).then(r => r.json()).catch(() => [])
      ]);
      setDokumente(Array.isArray(docs) ? docs.sort((a, b) => (b.created_date || '').localeCompare(a.created_date || '')) : []);
      setKlienten(Array.isArray(kl) ? kl : []);
      setJurPersonen(Array.isArray(jp) ? jp : []);
      setAufgaben(Array.isArray(auf) ? auf : []);
      setMassnahmenkatalog(Array.isArray(katalog) ? katalog : []);
      setMassnahmenGruppen(Array.isArray(gruppen) ? gruppen : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  // KI-Analyse für alle Dokumente laden (wenn KI-Modus aktiv)
  const loadKiAnalyse = async () => {
    setKiAnalyseLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/assistent/posteingang/mit-analyse`);
      const data = await response.json();
      if (data.dokumente) {
        setDokumenteMitAnalyse(data.dokumente);
      }
    } catch (e) {
      console.error('KI-Analyse laden fehlgeschlagen:', e);
    } finally {
      setKiAnalyseLoading(false);
    }
  };

  // Automatisch KI-Analyse laden wenn KI-Modus aktiviert wird
  useEffect(() => {
    if (kiModus) {
      loadKiAnalyse();
    }
  }, [kiModus]);

  // Handler für ausgeführte Aktionen aus PosteingangAutoAktion
  const handleAktionAusgefuehrt = (dokumentId, aktion, result) => {
    // Aktualisiere lokale Liste
    setDokumenteMitAnalyse(prev => 
      prev.filter(d => aktion === 'ablegen' ? d.id !== dokumentId : true)
    );
    // Auch Hauptliste aktualisieren
    if (aktion === 'ablegen') {
      loadData();
    }
  };

  // Klient zu Dokument zuordnen
  const handleKlientZuordnen = async (dokumentId, klientId) => {
    try {
      await betreuungAPI.updateDokument(dokumentId, { klient_id: klientId });
      toast.success('Klient zugeordnet');
      loadKiAnalyse();
    } catch (e) {
      toast.error('Fehler bei Zuordnung');
    }
  };

  // Listener: Wenn ein Dokument per Drag & Drop zugeordnet wird, Daten neu laden
  useEffect(() => {
    const handleZugeordnet = () => loadData();
    window.addEventListener('dokument-zugeordnet', handleZugeordnet);
    return () => window.removeEventListener('dokument-zugeordnet', handleZugeordnet);
  }, []);

  // Cross-Tab Event Listener: Aktualisiert Daten wenn in anderen Tabs Entities erstellt werden
  useEffect(() => {
    const handleEntityCreated = (e) => {
      const { type, data } = e.detail;
      if (type === 'person' && data) {
        setKlienten(prev => [...prev, data]);
        setForm(prev => ({ ...prev, klient_id: data.id }));
      } else if (type === 'institution' && data) {
        setJurPersonen(prev => [...prev, data]);
        setForm(prev => ({ ...prev, von_institution_id: data.id }));
      } else if (type === 'massnahme' && data) {
        setMassnahmen(prev => [...prev, data]);
        setForm(prev => ({ ...prev, massnahme_id: data.id }));
      } else if (type === 'aufgabe' && data) {
        setAufgaben(prev => [...prev, data]);
        setForm(prev => ({ ...prev, aufgabe_id: data.id }));
      }
    };
    window.addEventListener(TAB_EVENTS.ENTITY_CREATED, handleEntityCreated);
    return () => window.removeEventListener(TAB_EVENTS.ENTITY_CREATED, handleEntityCreated);
  }, []);

  // Athena-Status laden
  useEffect(() => {
    const loadAthenaStats = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/athena/status`);
        if (res.ok) {
          const data = await res.json();
          setAthenaStats(data.statistiken);
        }
      } catch (e) { /* stille Fehler */ }
    };
    loadAthenaStats();
  }, []);
  // Upload
  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        const result = await betreuungAPI.uploadDokument(file, { dokumententyp: 'posteingang' });
        toast.success(`${file.name} hochgeladen`);
      } catch { toast.error(`Fehler: ${file.name}`); }
    }
    setUploading(false);
    loadData();
  };

  // Dokument auswählen
  const selectDoc = async (dok) => {
    setSelectedDoc(dok);
    setAnalyseResult(null);
    setKiEmpfehlung(null);
    setForm({
      klient_id: dok.klient_id || '',
      von_institution_id: '',
      an_institution_id: '',
      aufgabe_id: '',
      ist_antwort_auf: '',
      prioritaet: '',
      tags: dok.tags || '',
      spaeter_bearbeiten: false,
      anderer_bearbeiten: false,
      person_informieren: false,
      termin_anlegen: false,
      notiz: dok.notiz || ''
    });
    
    // WICHTIG: Kontext für KI-Assistenten aktualisieren
    // So kann Athena das aktuell angezeigte Dokument "sehen"
    window.dispatchEvent(new CustomEvent('olympus-context-update', {
      detail: {
        typ: 'dokument',
        dokument: {
          id: dok.id,
          dateiname: dok.dateiname,
          titel: dok.titel || dok.dateiname,
          dokumententyp: dok.dokumententyp,
          ocr_text: dok.ocr_text || ''
        },
        seite: 'posteingang'
      }
    }));
    
    // Automatisch KI-Empfehlung laden
    loadKiEmpfehlung(dok);
    
    // Chat-Nachrichten für neues Dokument zurücksetzen
    setDocChatMessages([]);
    setDocChatInput('');
  };

  // Dokument-spezifischer KI-Chat
  const sendDocChatMessage = async () => {
    if (!docChatInput.trim() || !selectedDoc || docChatLoading) return;
    
    const userMessage = docChatInput.trim();
    setDocChatInput('');
    setDocChatMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setDocChatLoading(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nachricht: userMessage,
          modus: 'lokal',
          page_context: {
            typ: 'dokument',
            dokument: {
              id: selectedDoc.id,
              dateiname: selectedDoc.dateiname,
              titel: selectedDoc.titel || selectedDoc.dateiname,
              dokumententyp: selectedDoc.dokumententyp,
              ocr_text: selectedDoc.ocr_text || ''
            },
            seite: 'posteingang'
          }
        })
      });
      const data = await res.json();
      setDocChatMessages(prev => [...prev, { role: 'assistant', text: data.antwort || 'Keine Antwort' }]);
      
      // Bei Aktionen: Daten neu laden
      if (data.aktion?.typ === 'multi_erstellt' || data.aktion?.typ === 'klient_erstellen' || data.aktion?.typ === 'juristische_person_erstellen') {
        await loadData();
        toast.success('Daten wurden angelegt');
      }
    } catch (e) {
      console.error('Chat-Fehler:', e);
      setDocChatMessages(prev => [...prev, { role: 'assistant', text: '❌ Fehler bei der Kommunikation mit der KI.' }]);
    } finally {
      setDocChatLoading(false);
    }
  };

  // Maßnahmen des Klienten laden
  const loadMassnahmenForKlient = async (klientId) => {
    if (!klientId || klientId === 'bueroorganisation' || klientId === 'none') {
      setMassnahmen([]);
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/betreuung/klienten/${klientId}/massnahmen`);
      if (res.ok) {
        const data = await res.json();
        setMassnahmen(data || []);
      } else {
        setMassnahmen([]);
      }
    } catch (e) {
      console.error('Fehler beim Laden der Maßnahmen:', e);
      setMassnahmen([]);
    }
  };

  // Wenn Klient sich ändert, Maßnahmen laden
  useEffect(() => {
    if (form.klient_id) {
      loadMassnahmenForKlient(form.klient_id);
    } else {
      setMassnahmen([]);
    }
  }, [form.klient_id]);

  // KI-Empfehlung laden
  const loadKiEmpfehlung = async (dok) => {
    setLoadingKiEmpfehlung(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/wiki/analysiere-dokument`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dateiname: dok.dateiname,
          titel: dok.titel || dok.dateiname,
          ocr_text: dok.ocr_text || ''
        })
      });
      const empfehlung = await res.json();
      setKiEmpfehlung(empfehlung);
    } catch (e) {
      console.error('KI-Empfehlung Fehler:', e);
    } finally {
      setLoadingKiEmpfehlung(false);
    }
  };

  // Aktion aus KI-Empfehlung anwenden
  const applyKiEmpfehlung = async () => {
    if (!kiEmpfehlung?.ki_empfehlung) return;
    const emp = kiEmpfehlung.ki_empfehlung;
    
    // Lern-Aktion protokollieren
    try {
      await fetch(`${BACKEND_URL}/api/wiki/lernen/aktion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dokument_typ: emp.dokumenttyp || 'unbekannt',
          absender: emp.absender_erkannt || kiEmpfehlung.gefundener_absender,
          aktion: emp.empfohlene_aktion,
          details: emp.aufgabe_vorschlag || {}
        })
      });
    } catch (e) {
      console.error('Lern-Aktion Fehler:', e);
    }
    
    // Formular mit KI-Empfehlungen ausfüllen
    const newForm = { ...form };
    
    // ========== "BEZÜGLICH"-FELD AUTOMATISCH AUSFÜLLEN ==========
    // 1. Büroorganisation
    if (emp.bezueglich_vorschlag === 'bueroorganisation' || emp.ist_bueroorganisation) {
      newForm.klient_id = 'bueroorganisation';
      toast.info('📁 Für: Büroorganisation erkannt');
    }
    // 2. Bekannter Klient gefunden
    else if (emp.bezueglich_vorschlag === 'klient' && emp.gefundener_klient) {
      newForm.klient_id = emp.gefundener_klient.id;
      const klientName = `${emp.gefundener_klient.vorname || ''} ${emp.gefundener_klient.nachname || ''}`.trim();
      toast.success(`👤 Für: ${klientName} erkannt`);
    }
    // 3. Neue Person vorgeschlagen → Tab öffnen
    else if (emp.bezueglich_vorschlag === 'neue_person' && emp.person_zum_anlegen) {
      openNeuePersonDialog(emp.person_zum_anlegen);
      toast.info(`Neue Person "${emp.person_zum_anlegen}" wird angelegt`);
    }
    
    // Priorität setzen basierend auf Dringlichkeit
    if (emp.dringlichkeit === 'hoch') {
      newForm.prioritaet = 'hoch';
    } else if (emp.dringlichkeit === 'mittel') {
      newForm.prioritaet = 'mittel';
    } else {
      newForm.prioritaet = 'niedrig';
    }
    
    // Tags setzen basierend auf Dokumenttyp
    const tags = [];
    if (emp.dokumenttyp) tags.push(emp.dokumenttyp);
    if (emp.absender_erkannt) tags.push(emp.absender_erkannt);
    newForm.tags = tags.join(', ');
    
    // Aufgabe anlegen wenn empfohlen
    if (emp.empfohlene_aktion === 'aufgabe_anlegen') {
      newForm.termin_anlegen = true;
      
      if (emp.aufgabe_vorschlag) {
        // Notiz mit Aufgaben-Details füllen
        let notizText = `📋 KI-AUFGABE: ${emp.aufgabe_vorschlag.titel}\n`;
        if (emp.aufgabe_vorschlag.beschreibung) {
          notizText += `\n${emp.aufgabe_vorschlag.beschreibung}\n`;
        }
        if (emp.naechste_schritte?.length > 0) {
          notizText += `\n📌 Nächste Schritte:\n`;
          emp.naechste_schritte.forEach((schritt, i) => {
            notizText += `${i + 1}. ${schritt}\n`;
          });
        }
        if (emp.begruendung) {
          notizText += `\n💡 ${emp.begruendung}`;
        }
        newForm.notiz = notizText;
      }
    } else if (emp.empfohlene_aktion === 'weiterleiten') {
      // Weiterleiten
      newForm.anderer_bearbeiten = true;
      newForm.notiz = `🔄 Weiterleiten an: ${emp.weiterleiten_an || 'Nicht spezifiziert'}\n\n${emp.begruendung || ''}`;
    } else if (emp.empfohlene_aktion === 'zur_akte') {
      // Zur Akte - nur Notiz
      newForm.notiz = `📁 Zur Akte: ${emp.begruendung || 'Dokument ablegen'}`;
    } else if (emp.empfohlene_aktion === 'antwort_schreiben') {
      // Antwort schreiben
      newForm.spaeter_bearbeiten = true;
      newForm.notiz = `✉️ Antwort erforderlich\n\n${emp.begruendung || ''}`;
    }
    
    // Formular aktualisieren
    setForm(newForm);
    
    // KI-Empfehlung ausblenden
    setKiEmpfehlung(null);
    
    toast.success('Empfehlung übernommen - Formular ausgefüllt');
  };

  // Neue Person in eigenem Tab öffnen
  const openNeuePersonDialog = (prefillName = '') => {
    const nameParts = prefillName.trim().split(' ');
    const vorname = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : '';
    const nachname = nameParts.length > 1 ? nameParts.slice(-1)[0] : prefillName;
    openChildTab('/schnellerfassung/person', {
      title: 'Neue Person',
      forceNew: true,
      params: {
        vorname,
        nachname,
        dokumentVorschauUrl: selectedDoc?.datei_url ? `${BACKEND_URL}${selectedDoc.datei_url}` : null,
        dokumentName: selectedDoc?.dateiname || selectedDoc?.titel || null,
        ocrText: selectedDoc?.ocr_text || selectedDoc?.inhalt || null,
        dokumentDateityp: selectedDoc?.dateityp || selectedDoc?.dateiname || null,
        dokumentId: selectedDoc?.id || null,
      }
    });
  };

  // ========== NACHANALYSE MIT ECKDATEN ==========
  const handleNachanalyse = async () => {
    if (!selectedDoc?.id) {
      toast.error('Kein Dokument ausgewählt');
      return;
    }
    
    if (!nachanalyseEckdaten.nachname && !nachanalyseEckdaten.hinweis) {
      toast.error('Bitte geben Sie mindestens einen Nachnamen oder Hinweis ein');
      return;
    }
    
    setNachanalyseLoading(true);
    setNachanalyseErgebnis(null);
    
    try {
      const result = await betreuungAPI.nachanalyseMitEckdaten(selectedDoc.id, nachanalyseEckdaten);
      
      if (result.success) {
        setNachanalyseErgebnis(result);
        
        if (result.person_gefunden && result.gefundene_person) {
          const person = result.gefundene_person;
          toast.success(`Person gefunden: ${person.vorname || ''} ${person.nachname || ''}`.trim());
          
          // Wenn passender Klient in DB gefunden, automatisch auswählen
          if (result.matched_klient) {
            setForm(prev => ({ ...prev, klient_id: result.matched_klient.id }));
            toast.info(`Klient "${result.matched_klient.vorname} ${result.matched_klient.nachname}" automatisch ausgewählt`);
          }
          
          // Dokumentkategorie setzen falls erkannt
          if (result.dokument_kategorie) {
            setForm(prev => ({ ...prev, dokumentkategorie: result.dokument_kategorie }));
          }
        } else {
          toast.info('Keine eindeutige Person im Dokument gefunden');
        }
      } else {
        toast.error(result.error || 'Analyse fehlgeschlagen');
      }
    } catch (e) {
      console.error('Nachanalyse-Fehler:', e);
      toast.error('Fehler bei der Nachanalyse');
    } finally {
      setNachanalyseLoading(false);
    }
  };

  // ========== FORMULAR-ANALYSE ==========
  const handleFormularAnalyse = async () => {
    if (!selectedDoc?.id) {
      toast.error('Kein Dokument ausgewählt');
      return;
    }
    
    setFormularAnalyseLoading(true);
    setFormularAnalyseErgebnis(null);
    
    try {
      const result = await betreuungAPI.formularAnalyse(
        selectedDoc.id, 
        form.klient_id && form.klient_id !== 'bueroorganisation' ? form.klient_id : null
      );
      
      if (result.success) {
        setFormularAnalyseErgebnis(result);
        setShowFormularAnalyse(true);
        toast.success(`Formular analysiert: ${result.felder_ausgefuellt} Felder ausgefüllt, ${result.felder_offen} offen`);
        
        // Formular-Typ im Formular setzen
        if (result.formular_typ) {
          setForm(prev => ({ 
            ...prev, 
            dokumentkategorie: 'antraege_formulare',
            formular_typ: result.formular_typ 
          }));
        }
      } else {
        toast.error(result.error || 'Formular-Analyse fehlgeschlagen');
      }
    } catch (e) {
      console.error('Formular-Analyse-Fehler:', e);
      toast.error('Fehler bei der Formular-Analyse');
    } finally {
      setFormularAnalyseLoading(false);
    }
  };

  // Neue Institution in eigenem Tab öffnen
  const openNeueInstitutionDialog = (prefillName = '') => {
    openChildTab('/schnellerfassung/institution', {
      title: 'Neue Institution',
      forceNew: true,
      params: {
        name: prefillName,
        dokumentVorschauUrl: selectedDoc?.datei_url ? `${BACKEND_URL}${selectedDoc.datei_url}` : null,
        dokumentName: selectedDoc?.dateiname || selectedDoc?.titel || null,
        ocrText: selectedDoc?.ocr_text || selectedDoc?.inhalt || null,
      }
    });
  };

  // ========== BESCHEID-WIEDERVORLAGE ==========
  const handleBescheidWiedervorlage = async () => {
    if (!form.klient_id || form.klient_id === 'bueroorganisation') {
      toast.error('Bitte zuerst einen Betreuten auswählen');
      return;
    }
    if (!form.bescheid_gueltig_bis) {
      toast.error('Bitte Gültigkeitsdatum eingeben');
      return;
    }
    
    setWiedervorlageLoading(true);
    setWiedervorlageErstellt(null);
    
    try {
      const result = await betreuungAPI.createBescheidWiedervorlage(
        form.klient_id,
        form.bescheid_typ || 'sonstiges',
        form.bescheid_gueltig_bis,
        selectedDoc?.id,
        4  // 4 Wochen Vorlauf
      );
      
      if (result.success) {
        setWiedervorlageErstellt(result);
        toast.success(`Wiedervorlage erstellt: Fällig am ${new Date(result.wiedervorlage_datum).toLocaleDateString('de-DE')}`);
        
        // Aufgabenliste aktualisieren
        const aufgabenRes = await betreuungAPI.listAufgaben({ status: 'offen' });
        setAufgaben(aufgabenRes);
      }
    } catch (e) {
      console.error('Wiedervorlage-Fehler:', e);
      toast.error(e.message || 'Fehler beim Erstellen der Wiedervorlage');
    } finally {
      setWiedervorlageLoading(false);
    }
  };


  // Neue Institution speichern → wird direkt im NeueInstitutionDialog-Komponente gehandhabt

  // Neue Massnahme in eigenem Tab öffnen
  const openNeueMassnahmeDialog = () => {
    if (!form.klient_id || form.klient_id === 'bueroorganisation') {
      toast.error('Bitte zuerst einen Betreuten auswählen');
      return;
    }
    const klient = klienten.find(k => k.id === form.klient_id);
    openChildTab('/schnellerfassung/massnahme', {
      title: 'Neue Massnahme',
      forceNew: true,
      params: {
        klientId: form.klient_id,
        klientName: klient ? `${klient.vorname} ${klient.nachname}` : '',
        dokumentVorschauUrl: selectedDoc?.datei_url ? `${BACKEND_URL}${selectedDoc.datei_url}` : null,
        dokumentName: selectedDoc?.dateiname || selectedDoc?.titel || null,
        ocrText: selectedDoc?.ocr_text || selectedDoc?.inhalt || null,
        dokumentDateityp: selectedDoc?.dateityp || selectedDoc?.dateiname || null,
        dokumentId: selectedDoc?.id || null,
      }
    });
  };

  // Neue Aufgabe in eigenem Tab öffnen
  const openNeueAufgabeDialog = () => {
    const klient = klienten.find(k => k.id === form.klient_id);
    openChildTab('/schnellerfassung/aufgabe', {
      title: 'Neue Aufgabe',
      forceNew: true,
      params: {
        klientId: form.klient_id || null,
        klientName: klient ? `${klient.vorname} ${klient.nachname}` : '',
        dokumentId: selectedDoc?.id || null,
        dokumentName: selectedDoc?.dateiname || selectedDoc?.titel || null,
        dokumentVorschauUrl: selectedDoc?.datei_url ? `${BACKEND_URL}${selectedDoc.datei_url}` : null,
        ocrText: selectedDoc?.ocr_text || selectedDoc?.inhalt || null,
        dokumentDateityp: selectedDoc?.dateityp || selectedDoc?.dateiname || null,
        massnahmeId: form.massnahme_id || null,
      }
    });
  };

  // Dokument löschen
  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Dokument endgültig löschen?')) return;
    try {
      await betreuungAPI.deleteDokument(docId);
      toast.success('Dokument gelöscht');
      if (selectedDoc?.id === docId) {
        setSelectedDoc(null);
      }
      loadData();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  // KI-Analyse starten
  const handleAnalyse = async () => {
    if (!selectedDoc) return;
    setAnalysing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/assistent/analyse-dokument`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dokument_id: selectedDoc.id })
      });
      const result = await response.json();
      if (result.success) {
        setAnalyseResult(result);
        // Auto-fill aus Analyse
        const updates = {};
        if (result.erkannter_klient_id) {
          updates.klient_id = result.erkannter_klient_id;
        }
        if (result.vorgeschlagene_aufgabe_id) {
          updates.aufgabe_id = result.vorgeschlagene_aufgabe_id;
        }
        if (Object.keys(updates).length > 0) {
          setForm(p => ({ ...p, ...updates }));
          toast.success(`KI-Zuordnung: ${result.erkannter_klient_name || 'Klient erkannt'}${result.vorgeschlagene_aufgabe_titel ? ` → Aufgabe: ${result.vorgeschlagene_aufgabe_titel}` : ''}`);
        } else {
          toast.success('Analyse abgeschlossen');
        }
      } else {
        toast.error(result.error || 'Analyse fehlgeschlagen');
      }
    } catch { toast.error('Analysefehler'); }
    finally { setAnalysing(false); }
  };

  // Speichern & Weiter
  const handleSave = async (weiter = false) => {
    if (!selectedDoc) return;
    try {
      let finalKlientId = form.klient_id;
      
      // Wenn "Neue Person" ausgewählt wurde, diese zuerst anlegen
      if (form.klient_id?.startsWith('NEU:')) {
        const personName = form.klient_id.replace('NEU:', '');
        const nameParts = personName.trim().split(' ');
        const vorname = nameParts.slice(0, -1).join(' ') || '';
        const nachname = nameParts.slice(-1)[0] || personName;
        
        try {
          const neuePersonRes = await betreuungAPI.createKlient({
            vorname: vorname,
            nachname: nachname,
            status: 'aktiv'
          });
          finalKlientId = neuePersonRes.id;
          toast.success(`Neue Person "${personName}" angelegt`);
          
          // Klienten-Liste aktualisieren
          setKlienten(prev => [...prev, neuePersonRes]);
        } catch (e) {
          toast.error('Fehler beim Anlegen der Person');
          console.error(e);
          return;
        }
      }
      
      // Büroorganisation → keine klient_id speichern
      if (finalKlientId === 'bueroorganisation') {
        finalKlientId = null;
      }
      
      // Dokument aktualisieren
      await betreuungAPI.updateDokument(selectedDoc.id, {
        klient_id: finalKlientId || null,
        dokumententyp: 'zugeordnet',
        tags: form.tags,
        notiz: form.notiz,
        ist_bueroorganisation: form.klient_id === 'bueroorganisation'
      });
      
      // Verknüpfung mit Aufgabe
      if (form.aufgabe_id) {
        // Verlaufseintrag hinzufügen
        await betreuungAPI.createAufgabenVerlauf(form.aufgabe_id, {
          text: `Dokument "${selectedDoc.titel || selectedDoc.dateiname}" zugeordnet`,
          typ: 'dokument'
        });
      }
      
      // Termin anlegen wenn gewünscht
      if (form.termin_anlegen && (finalKlientId || form.klient_id === 'bueroorganisation')) {
        const klient = klienten.find(k => k.id === finalKlientId);
        await betreuungAPI.createAufgabe({
          titel: `Dokument bearbeiten: ${selectedDoc.titel || selectedDoc.dateiname}`,
          klient_id: finalKlientId || null,
          betreuter_name: klient ? `${klient.vorname} ${klient.nachname}` : (form.klient_id === 'bueroorganisation' ? 'Büroorganisation' : ''),
          prioritaet: form.prioritaet || 'mittel',
          status: 'offen',
          beschreibung: form.notiz || ''
        });
        toast.success('Aufgabe angelegt');
      }
      
      toast.success('Gespeichert');

      // Athena Lern-Aktion protokollieren (nur pseudonymisierte Daten: Typ + Institution + Aktion)
      try {
        const absenderInstitution = jurPersonen.find(j => j.id === form.von_institution_id)?.name || null;
        const lernRes = await fetch(`${BACKEND_URL}/api/wiki/lernen/aktion`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dokument_typ: form.kategorie || selectedDoc.dokumententyp || 'unbekannt',
            absender: absenderInstitution,
            aktion: form.aufgabe_id ? 'aufgabe_anlegen' : (form.massnahme_id ? 'massnahme_zuordnen' : 'zur_akte'),
            details: {}
          })
        });
        if (lernRes.ok) {
          const lernData = await lernRes.json();
          // Lernmodus kurz aktivieren
          setAthenaLernt(true);
          setTimeout(() => setAthenaLernt(false), 3000);
          // Wenn Muster erkannt: anzeigen
          if (lernData.muster_erkannt && lernData.muster) {
            setNeuesMuster(lernData.muster);
            // Athena-Stats aktualisieren
            const statusRes = await fetch(`${BACKEND_URL}/api/athena/status`);
            if (statusRes.ok) setAthenaStats((await statusRes.json()).statistiken);
          }
        }
      } catch (e) { /* Lernfehler nicht dem User anzeigen */ }

      if (weiter) {
        // Nächstes Dokument auswählen
        const idx = dokumente.findIndex(d => d.id === selectedDoc.id);
        if (idx < dokumente.length - 1) {
          selectDoc(dokumente[idx + 1]);
        } else {
          setSelectedDoc(null);
          loadData();
        }
      } else {
        setSelectedDoc(null);
        loadData();
      }
    } catch (e) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Aufgaben nach Klient filtern
  const filteredAufgaben = form.klient_id 
    ? aufgaben.filter(a => a.klient_id === form.klient_id)
    : aufgaben;

  // Stapelverarbeitung - Alle Dokumente auf einmal analysieren
  const handleBatchAnalyse = async () => {
    if (dokumente.length === 0) {
      toast.info('Keine Dokumente zum Analysieren');
      return;
    }
    
    setBatchAnalysing(true);
    setBatchResults(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/wiki/stapelverarbeitung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const result = await response.json();
      
      if (result.success) {
        setBatchResults(result);
        setShowBatchDialog(true);
        
        const { zusammenfassung } = result;
        toast.success(
          `${result.anzahl_analysiert} Dokumente analysiert: ${zusammenfassung.hoch} dringend, ${zusammenfassung.mittel} mittel, ${zusammenfassung.niedrig} niedrig`,
          { duration: 5000 }
        );
      } else {
        toast.error('Stapelverarbeitung fehlgeschlagen');
      }
    } catch (e) {
      console.error('Batch-Fehler:', e);
      toast.error('Fehler bei Stapelverarbeitung');
    } finally {
      setBatchAnalysing(false);
    }
  };

  // Dokument aus Batch-Ergebnis auswählen
  const selectFromBatch = (result) => {
    const dok = dokumente.find(d => d.id === result.dokument_id);
    if (dok) {
      selectDoc(dok);
      // KI-Empfehlung aus Batch-Ergebnis übernehmen
      if (result.analyse_komplett) {
        setKiEmpfehlung(result.analyse_komplett);
      }
    }
    setShowBatchDialog(false);
  };

  // Anzahl zuordenbarer Dokumente berechnen
  const zuordenbarCount = batchResults?.ergebnisse?.filter(e => e.gefundener_klient?.id)?.length || 0;

  // Auto-Zuordnung Optionen
  const [autoZuordnungOptionen, setAutoZuordnungOptionen] = useState({
    auch_aufgaben_erstellen: true
  });

  // Auto-Zuordnung starten
  const handleAutoZuordnung = async () => {
    if (!batchResults?.ergebnisse) return;
    
    setAutoZuordnungRunning(true);
    setShowAutoZuordnungConfirm(false);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/wiki/auto-zuordnung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ergebnisse: batchResults.ergebnisse,
          nur_mit_klient: true,
          auch_titel_uebernehmen: true,
          auch_aufgaben_erstellen: autoZuordnungOptionen.auch_aufgaben_erstellen
        })
      });
      const result = await response.json();
      
      if (result.success) {
        setAutoZuordnungResult(result);
        toast.success(result.zusammenfassung, { duration: 5000 });
        
        try {
          await loadData();
        } catch (loadErr) {
          console.warn('Dokumentenliste konnte nicht neu geladen werden:', loadErr);
        }
      } else {
        toast.error('Auto-Zuordnung fehlgeschlagen');
      }
    } catch (e) {
      console.error('Auto-Zuordnung Fehler:', e);
      toast.error('Fehler bei Auto-Zuordnung');
    } finally {
      setAutoZuordnungRunning(false);
    }
  };

  // Nachricht erfassen & analysieren (WhatsApp/Telefon)
  const handleNachrichtAnalysieren = async () => {
    if (!nachrichtText.trim()) {
      toast.error('Bitte Nachrichtentext eingeben');
      return;
    }
    setNachrichtLoading(true);
    setNachrichtErgebnis(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/wiki/nachricht-analysieren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: nachrichtText,
          quelle: nachrichtQuelle,
          absender_name: nachrichtAbsender
        })
      });
      const data = await res.json();
      if (data.success) {
        setNachrichtErgebnis(data);
        toast.success('Nachricht analysiert und als Dokument erfasst');
        loadData(); // Dokumentenliste aktualisieren
      } else {
        toast.error(data.error || 'Analyse fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Fehler bei der Analyse');
    } finally {
      setNachrichtLoading(false);
    }
  };

  const resetNachrichtForm = () => {
    setNachrichtText('');
    setNachrichtAbsender('');
    setNachrichtErgebnis(null);
    setShowNachrichtForm(false);
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="posteingang-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Inbox className="w-6 h-6" />
            Posteingang
          </h1>
          <p className="text-slate-500 text-sm">{dokumente.length} Dokument(e) · Butler21-Style</p>
        </div>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.tiff" className="hidden"
            onChange={e => { handleUpload(e.target.files); e.target.value = ''; }} />
          <Button 
            variant="outline" 
            onClick={() => setShowNachrichtForm(v => !v)}
            data-testid="nachricht-erfassen-btn"
          >
            <Phone className="w-4 h-4 mr-2" />
            Nachricht erfassen
          </Button>
          <Button 
            variant="outline"
            onClick={() => openComposer(null)}
            className="border-purple-200 text-purple-700 hover:bg-purple-50"
            data-testid="neue-email-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            Neue E-Mail
          </Button>
          <Button 
            variant={kiModus ? "default" : "outline"}
            onClick={() => setKiModus(!kiModus)} 
            className={kiModus ? "bg-violet-600 hover:bg-violet-700" : ""}
            data-testid="ki-modus-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {kiModus ? 'KI-Modus aktiv' : 'KI-Modus'}
          </Button>
          <Button 
            variant="outline" 
            onClick={handleBatchAnalyse} 
            disabled={batchAnalysing || dokumente.length === 0}
            data-testid="batch-analyse-btn"
          >
            {batchAnalysing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {batchAnalysing ? 'Claude analysiert...' : 'Alle analysieren'}
          </Button>
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Hochladen
          </Button>
        </div>
      </div>

      {/* Nachricht erfassen Formular */}
      {showNachrichtForm && (
        <div className="bg-white rounded-xl border border-violet-200 p-4 mb-4 shrink-0" data-testid="nachricht-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-violet-500" />
              Nachricht erfassen (WhatsApp / Telefonnotiz)
            </h3>
            <button onClick={resetNachrichtForm} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-[1fr_1fr_2fr] gap-3">
            <div>
              <Label className="text-xs text-slate-600">Quelle</Label>
              <Select value={nachrichtQuelle} onValueChange={setNachrichtQuelle}>
                <SelectTrigger className="h-8 text-xs" data-testid="nachricht-quelle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="telefon">Telefonnotiz</SelectItem>
                  <SelectItem value="email">E-Mail</SelectItem>
                  <SelectItem value="notiz">Sonstige Notiz</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Absender (optional)</Label>
              <Input 
                value={nachrichtAbsender} 
                onChange={e => setNachrichtAbsender(e.target.value)}
                placeholder="z.B. Frau Müller, AOK..."
                className="h-8 text-xs"
                data-testid="nachricht-absender"
              />
            </div>
            <div className="row-span-2">
              <Label className="text-xs text-slate-600">Nachrichtentext</Label>
              <Textarea 
                value={nachrichtText}
                onChange={e => setNachrichtText(e.target.value)}
                placeholder="Nachricht hier eingeben oder einfügen..."
                rows={3}
                className="text-xs resize-none"
                data-testid="nachricht-text"
              />
            </div>
          </div>

          {/* Ergebnis */}
          {nachrichtErgebnis?.ki_empfehlung && (
            <div className="mt-3 p-3 bg-violet-50 rounded-lg border border-violet-200 text-sm">
              <p className="font-medium text-violet-700 mb-1">Claude-Analyse:</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {nachrichtErgebnis.ki_empfehlung.absender_erkannt && (
                  <p><span className="text-slate-500">Absender:</span> {nachrichtErgebnis.ki_empfehlung.absender_erkannt}</p>
                )}
                {nachrichtErgebnis.ki_empfehlung.betrifft_person && (
                  <p><span className="text-slate-500">Betreuter:</span> {nachrichtErgebnis.ki_empfehlung.betrifft_person}</p>
                )}
                {nachrichtErgebnis.ki_empfehlung.handlungsauftrag && (
                  <p className="col-span-2"><span className="text-slate-500">Handlungsauftrag:</span> {nachrichtErgebnis.ki_empfehlung.handlungsauftrag}</p>
                )}
                {nachrichtErgebnis.ki_empfehlung.frist && (
                  <p><span className="text-slate-500">Frist:</span> <span className="text-red-600 font-medium">{nachrichtErgebnis.ki_empfehlung.frist}</span></p>
                )}
                {nachrichtErgebnis.ki_empfehlung.dringlichkeit && (
                  <p><span className="text-slate-500">Dringlichkeit:</span> {nachrichtErgebnis.ki_empfehlung.dringlichkeit}</p>
                )}
              </div>
              {nachrichtErgebnis.gefundener_klient && (
                <p className="mt-1 text-xs text-green-700">
                  <Check className="w-3 h-3 inline mr-1" />
                  Betreuter erkannt: {nachrichtErgebnis.gefundener_klient.vorname} {nachrichtErgebnis.gefundener_klient.nachname}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={resetNachrichtForm}>Abbrechen</Button>
            <Button 
              size="sm" 
              onClick={handleNachrichtAnalysieren}
              disabled={nachrichtLoading || !nachrichtText.trim()}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="nachricht-analysieren-btn"
            >
              {nachrichtLoading ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-1" />
              )}
              {nachrichtLoading ? 'Claude analysiert...' : 'Analysieren & Erfassen'}
            </Button>
          </div>
        </div>
      )}

      {/* E-Mail Composer */}
      {composerOpen && (
        <div className="mb-4 shrink-0">
          <EmailComposer
            replyTo={composerReplyTo}
            onClose={() => { setComposerOpen(false); setComposerReplyTo(null); }}
            onSent={loadData}
            klientId={selectedDoc?.klient_id}
          />
        </div>
      )}

      {/* Kanal-Filter */}
      <div className="flex items-center gap-1.5 mb-3 shrink-0 flex-wrap" data-testid="kanal-filter">
        {[
          { key: 'alle', label: 'Alle', icon: Inbox, color: 'text-slate-600' },
          { key: 'email', label: 'E-Mail', icon: Mail, color: 'text-purple-600' },
          { key: 'fax', label: 'Fax', icon: Printer, color: 'text-amber-600' },
          { key: 'egvp', label: 'EGVP', icon: Building2, color: 'text-blue-600' },
          { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'text-green-600' },
          { key: 'verpasste_anrufe', label: 'Verpasste Anrufe', icon: PhoneMissed, color: 'text-red-600' },
        ].map(f => {
          const count = f.key === 'alle'
            ? dokumente.length
            : f.key === 'verpasste_anrufe'
              ? dokumente.filter(d => d.quelle === 'telefon').length
              : dokumente.filter(d => d.kanal === f.key || d.quelle === f.key).length;
          return (
            <Button
              key={f.key}
              variant={kanalFilter === f.key ? 'default' : 'outline'}
              size="sm"
              className={`h-8 text-xs ${kanalFilter === f.key ? '' : 'bg-white'}`}
              onClick={() => setKanalFilter(f.key)}
              data-testid={`filter-${f.key}`}
            >
              <f.icon className={`w-3.5 h-3.5 mr-1 ${kanalFilter === f.key ? 'text-white' : f.color}`} />
              {f.label}
              {count > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{count}</Badge>
              )}
            </Button>
          );
        })}
      </div>

      {/* KI-MODUS: Intelligente Posteingang-Ansicht */}
      {kiModus ? (
        <div className="flex-1 flex flex-col min-h-0 bg-white rounded-xl border border-violet-200 overflow-hidden">
          <div className="p-4 border-b border-violet-200 bg-gradient-to-r from-violet-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                <h2 className="text-lg font-semibold text-slate-800">KI-Posteingang</h2>
                <Badge className="bg-violet-100 text-violet-700 border-violet-200">
                  {dokumenteMitAnalyse.length} Dokumente
                </Badge>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={loadKiAnalyse}
                disabled={kiAnalyseLoading}
              >
                {kiAnalyseLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1" />
                )}
                Aktualisieren
              </Button>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Alle Dokumente wurden automatisch analysiert. Klicken Sie auf eine Handlungsempfehlung, um sie auszuführen.
            </p>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {kiAnalyseLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-violet-500 mb-3" />
                <p className="text-slate-500">KI analysiert Dokumente...</p>
              </div>
            ) : dokumenteMitAnalyse.length === 0 ? (
              <div className="text-center py-12">
                <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">Alle Dokumente bearbeitet!</p>
                <p className="text-sm text-slate-400">Der Posteingang ist leer.</p>
              </div>
            ) : (
              dokumenteMitAnalyse.map(dok => (
                <PosteingangAutoAktion
                  key={dok.id}
                  dokument={dok}
                  analyse={dok.analyse}
                  onAktionAusgefuehrt={handleAktionAusgefuehrt}
                  onKlientZuordnen={handleKlientZuordnen}
                />
              ))
            )}
          </div>
        </div>
      ) : (
        /* Klassische 3-Spalten Ansicht */
        <>
      {/* 3-Spalten Layout */}
      <div className="flex-1 flex gap-4 min-h-0">
        
        {/* LINKS: Dokumentenliste */}
        <div className="w-72 shrink-0 flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700">Scaneingang ({gefilterteDokumente.length}{kanalFilter !== 'alle' ? ` / ${dokumente.length}` : ''})</p>
              <Button
                variant={bulkMode ? 'secondary' : 'ghost'}
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => { setBulkMode(!bulkMode); setBulkSelected(new Set()); setBulkKlientId(''); }}
                data-testid="toggle-bulk-mode"
                title="Mehrere Dokumente gleichzeitig zuordnen"
              >
                {bulkMode ? <X className="w-3 h-3 mr-1" /> : <CheckSquare className="w-3 h-3 mr-1" />}
                {bulkMode ? 'Abbrechen' : 'Bulk'}
              </Button>
            </div>
            
            {/* Bulk-Aktionsleiste */}
            {bulkMode && (
              <div className="mt-2 space-y-2" data-testid="bulk-action-bar">
                <div className="flex items-center gap-2">
                  <button
                    className="text-xs text-blue-600 hover:underline"
                    onClick={toggleBulkAll}
                    data-testid="bulk-select-all"
                  >
                    {bulkSelected.size === gefilterteDokumente.filter(d => !d.klient_id).length && bulkSelected.size > 0
                      ? 'Alle abwählen' : 'Alle unzugeordneten wählen'}
                  </button>
                  <Badge variant="outline" className="text-xs">
                    {bulkSelected.size} gewählt
                  </Badge>
                </div>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Klient suchen..."
                    className="w-full text-xs pl-7 pr-2 py-1.5 border rounded bg-white"
                    value={bulkSearch}
                    onChange={e => setBulkSearch(e.target.value)}
                    data-testid="bulk-klient-search"
                  />
                </div>
                {bulkSearch && (
                  <div className="max-h-32 overflow-y-auto border rounded bg-white" data-testid="bulk-klient-results">
                    {filteredBulkKlienten.slice(0, 8).map(k => (
                      <button key={k.id}
                        className={`w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 ${bulkKlientId === k.id ? 'bg-blue-100 font-medium' : ''}`}
                        onClick={() => { setBulkKlientId(k.id); setBulkSearch(`${k.vorname} ${k.nachname}`); }}
                        data-testid={`bulk-klient-${k.id}`}
                      >
                        {k.vorname} {k.nachname}
                        {k.aktenzeichen && <span className="text-slate-400 ml-1">({k.aktenzeichen})</span>}
                      </button>
                    ))}
                  </div>
                )}
                <Button
                  size="sm"
                  className="w-full h-7 text-xs"
                  disabled={bulkSelected.size === 0 || !bulkKlientId || bulkSaving}
                  onClick={handleBulkZuordnen}
                  data-testid="bulk-zuordnen-btn"
                >
                  {bulkSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserCheck className="w-3 h-3 mr-1" />}
                  {bulkSelected.size} Dok. zuordnen
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="w-full h-7 text-xs"
                  disabled={bulkSelected.size === 0 || bulkDeleting}
                  onClick={handleBulkDelete}
                  data-testid="bulk-delete-btn"
                >
                  {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                  {bulkSelected.size} Dok. löschen
                </Button>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto"
            onDrop={e => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
            onDragOver={e => e.preventDefault()}>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : gefilterteDokumente.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {gefilterteDokumente.map(dok => (
                  bulkMode ? (
                    <div
                      key={dok.id}
                      className={`flex items-center gap-1 hover:bg-blue-50 transition-colors cursor-pointer ${
                        bulkSelected.has(dok.id) ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleBulkSelect(dok.id)}
                      data-testid={`bulk-item-${dok.id}`}
                    >
                      <div className="shrink-0 pl-2 py-2">
                        <input
                          type="checkbox"
                          checked={bulkSelected.has(dok.id)}
                          readOnly
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600"
                        />
                      </div>
                      <div className="flex-1 min-w-0 py-2 pr-2">
                        <p className="text-xs font-medium text-slate-800 truncate">
                          {dok.dateiname || dok.titel || 'Dokument'}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[10px] text-slate-400">
                            {dok.created_date ? new Date(dok.created_date).toLocaleDateString('de-DE') : ''}
                          </span>
                          {dok.klient_name && (
                            <span className="text-[10px] text-green-600 flex items-center gap-0.5">
                              <User className="w-2.5 h-2.5" />{dok.klient_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                  <DraggableDocItem
                    key={dok.id}
                    dok={dok}
                    isSelected={selectedDoc?.id === dok.id}
                    onSelect={selectDoc}
                    onDelete={handleDeleteDoc}
                    kiEmpfehlung={selectedDoc?.id === dok.id ? kiEmpfehlung : null}
                    onAssigned={loadData}
                    klienten={klienten}
                  />
                  )
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-400">
                <Inbox className="w-10 h-10 mx-auto mb-2" />
                <p className="text-sm">Dokumente hierher ziehen</p>
              </div>
            )}
          </div>
        </div>

        {/* MITTE: Zuordnungsformular */}
        <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden min-w-0">
          {selectedDoc ? (
            <>
              {/* Header: Dateiname + Aktionen */}
              <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                  <span className="text-sm font-medium truncate">{selectedDoc.dateiname || 'Dokument'}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {(selectedDoc.quelle === 'email' || selectedDoc.kanal === 'email') && (
                    <Button size="sm" variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50"
                      onClick={() => openComposer(selectedDoc)} data-testid="reply-email-btn">
                      <Reply className="w-4 h-4 mr-1" /> Antworten
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={handleAnalyse} disabled={analysing}>
                    {analysing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    KI-Analyse
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    onClick={() => handleDeleteDoc(selectedDoc.id)}
                    data-testid="delete-selected-doc"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Löschen
                  </Button>
                </div>
              </div>

              <PosteingangZuordnungsForm
                form={form} setForm={setForm}
                selectedDoc={selectedDoc}
                klienten={klienten} jurPersonen={jurPersonen}
                massnahmen={massnahmen} aufgaben={aufgaben}
                kiEmpfehlung={kiEmpfehlung}
                showKlientSearch={showKlientSearch} setShowKlientSearch={setShowKlientSearch}
                klientSearchQuery={klientSearchQuery} setKlientSearchQuery={setKlientSearchQuery}
                openNeuePersonDialog={openNeuePersonDialog}
                openNeueInstitutionDialog={openNeueInstitutionDialog}
                openNeueMassnahmeDialog={openNeueMassnahmeDialog}
                openNeueAufgabeDialog={openNeueAufgabeDialog}
                showNachanalyse={showNachanalyse} setShowNachanalyse={setShowNachanalyse}
                nachanalyseEckdaten={nachanalyseEckdaten} setNachanalyseEckdaten={setNachanalyseEckdaten}
                nachanalyseLoading={nachanalyseLoading} nachanalyseErgebnis={nachanalyseErgebnis}
                handleNachanalyse={handleNachanalyse}
                showFormularAnalyse={showFormularAnalyse} setShowFormularAnalyse={setShowFormularAnalyse}
                formularAnalyseLoading={formularAnalyseLoading} formularAnalyseErgebnis={formularAnalyseErgebnis}
                handleFormularAnalyse={handleFormularAnalyse}
                wiedervorlageLoading={wiedervorlageLoading} wiedervorlageErstellt={wiedervorlageErstellt}
                handleBescheidWiedervorlage={handleBescheidWiedervorlage}
                setKiEmpfehlung={setKiEmpfehlung} loadingKiEmpfehlung={loadingKiEmpfehlung}
                analyseResult={analyseResult} applyKiEmpfehlung={applyKiEmpfehlung}
                showDocChat={showDocChat} setShowDocChat={setShowDocChat}
                docChatInput={docChatInput} setDocChatInput={setDocChatInput}
                docChatMessages={docChatMessages} docChatLoading={docChatLoading}
                sendDocChatMessage={sendDocChatMessage}
                athenaLernt={athenaLernt} neuesMuster={neuesMuster}
                setNeuesMuster={setNeuesMuster} athenaStats={athenaStats}
                handleSave={handleSave}
                onVerlassen={() => setSelectedDoc(null)}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <ChevronRight className="w-10 h-10 mx-auto mb-2" />
                <p>Dokument links auswählen</p>
              </div>
            </div>
          )}
        </div>

        {/* RECHTS: Dokumentenvorschau */}
        <PosteingangVorschau selectedDoc={selectedDoc} />

      </div>
        </>
      )}

      {/* Stapelverarbeitung + Auto-Zuordnung (Inline-Cards) */}
      <PosteingangBatchDialoge
        showBatchDialog={showBatchDialog}
        setShowBatchDialog={setShowBatchDialog}
        batchResults={batchResults}
        autoZuordnungRunning={autoZuordnungRunning}
        autoZuordnungResult={autoZuordnungResult}
        setAutoZuordnungResult={setAutoZuordnungResult}
        zuordenbarCount={zuordenbarCount}
        selectFromBatch={selectFromBatch}
        setShowAutoZuordnungConfirm={setShowAutoZuordnungConfirm}
        showAutoZuordnungConfirm={showAutoZuordnungConfirm}
        autoZuordnungOptionen={autoZuordnungOptionen}
        setAutoZuordnungOptionen={setAutoZuordnungOptionen}
        handleAutoZuordnung={handleAutoZuordnung}
      />
    </div>
  );
}
